import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import * as moment from 'moment';
import { v4 as uuid } from 'uuid';
import { bindActionCreators } from 'redux';
import memoizeOne from 'memoize-one';
import randomColor from 'randomcolor';
import MapViewPage from './MapViewPage';
import userIco from '../../icons/userIcon.png';
import { TASK_SOURCE } from '../../HelperMethods/constants';
import { STATUS_LIST } from '../../HelperMethods/enums';
import * as TaskUtilities from '../../HelperMethods/TaskUtilities';
import { getFinalCenterAndZoomInMapView } from '../../HelperMethods/LocalStorageUtils';
import { isDateInBetweenDateRange, parseJSONStringToObject } from '../../HelperMethods/UtilityFunctions';
import StopCard from '../common/stopCard/StopCard';
import ApiService from '../../service/apiService';
import logUtils from '../../HelperMethods/LogUtils';
import ActualVsPlannedCard from './actualVsPlannedComponents/ActualVsPlannedCard';
import * as manageMapViewActionFunction from './redux/actions';
import { closeView, CONTAINERS, resetView } from '../common/cardNavigationComponent/cardNavigationRedux';
import { PATH_TYPE, STOP_TYPE, USER_LOCATION_TYPE } from './constants';
import PortalComponent from '../common/PortalComponent';
import CustomDropdown from '../common/CustomDropdown';
import ActualVsPlannedButton from './actualVsPlannedComponents/ActualVsPlannedButton';
import ReCenterButton from './ReCenterButton';
import CardNavigationComponent from '../common/cardNavigationComponent/CardNavigationComponent';
import { filterOutStopsOnSelectedUser, getPolygon } from './helper';

class ManageMapView extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      paths: [],
      showActualVsPlannedView: false,
      selectedRun: null,
      polygons: [],
    };
    this.stopInfoDict = {};
    this.taskListCard = React.createRef();
    this.mapComponent = React.createRef();
    this.userCard = React.createRef();
  }

  async componentDidMount() {
    const { selectedUserIds, selectedDateRange, manageMapViewActions, isLocationServiceEnabled, selectedFieldId } =
      this.props;

    const { startDate, endDate } = selectedDateRange;
    this.loadStopsDictAndPaths();
    if (selectedUserIds.length === 1 && startDate === endDate) {
      manageMapViewActions.loadRouteData(selectedUserIds[0], startDate);
    }
    if (isLocationServiceEnabled) {
      await manageMapViewActions.loadUsersLocations(selectedFieldId);
      this.loadStopsDictAndPaths();
      setTimeout(() => {
        this.determineToRecenterMap(this.props, false);
      });
    }
  }

  componentDidUpdate(prevProps) {
    // this flag is to prevent erroneous behaviour when selected user is changed but task length is same
    const {
      users,
      tasks,
      selectedUserIds,
      selectedDateRange,
      useCurrentLocation,
      manageMapViewActions,
      selectedStopId,
      stopsDict,
      isStopMarkerClicked,
      // usersLocationInfo,
    } = this.props;

    const {
      users: prevUsers,
      tasks: prevTasks,
      selectedUserIds: prevSelectedUserIds,
      selectedDateRange: prevSelectedDateRange,
      useCurrentLocation: prevUseCurrentLocation,
      selectedStopId: prevSelectedStopId,
      // usersLocationInfo: prevUsersLocationInfo,
    } = prevProps;
    if (
      users !== prevUsers ||
      selectedDateRange !== prevSelectedDateRange ||
      useCurrentLocation !== prevUseCurrentLocation
    ) {
      // if users or tasks or selectedUserIds changes, reload the stopsDict and paths
      this.loadStopsDictAndPaths();
      setTimeout(() => {
        console.log('componentDidUpdate');
        this.determineToRecenterMap(prevProps);
      });
    }
    // if (usersLocationInfo !== prevUsersLocationInfo) {
    //   this.loadStopsDictAndPaths();
    //   setTimeout(() => {
    //     this.determineToRecenterMap(prevProps, false);
    //   });
    // }
    if (tasks !== prevTasks) {
      this.loadStopsDictAndPaths();
    }
    if (selectedUserIds !== prevSelectedUserIds || selectedDateRange !== prevSelectedDateRange) {
      const { startDate, endDate } = selectedDateRange;
      if (selectedUserIds.length === 1 && startDate === endDate) {
        this.onRunSelect({ data: null });
        manageMapViewActions.loadRouteData(selectedUserIds[0], startDate);
      } else {
        this.hideActualVsPlannedView();
      }
    }
    if (selectedStopId !== prevSelectedStopId && !isStopMarkerClicked) {
      this.recenterMap(selectedStopId && stopsDict[selectedStopId] && [stopsDict[selectedStopId].location]);
    }
    if (selectedUserIds !== prevSelectedUserIds) {
      setTimeout(this.recenterMap);
    }
  }

  componentWillUnmount() {
    const { closeNavigationView } = this.props;
    closeNavigationView(CONTAINERS.MAP_VIEW);
  }

  getPathObjectsForSelectedUsers = memoizeOne((stopsDict, pathObjects, selectedUserIds) => {
    if (selectedUserIds?.length > 0) {
      return pathObjects
        .filter((pathObject) => selectedUserIds.includes(pathObject.userId))
        .map((pathObject) => ({
          ...pathObject,
          path: pathObject.path.map((stopId) => _.get(stopsDict, `${stopId}.location`)),
        }));
    }
    return [];
  });

  getMarkersOnSelectedUsers = memoizeOne((stopsDict, selectedUserIds) => {
    const clonedStopsDict = _.clone(stopsDict);
    if (selectedUserIds?.length > 0) {
      filterOutStopsOnSelectedUser(clonedStopsDict, selectedUserIds);
    } else {
      this.filterOutUserStartEndStops(clonedStopsDict);
    }
    return Object.values(clonedStopsDict);
  });

  toggleActualVsPlanned = () => {
    const { closeNavigationView } = this.props;
    this.setState(
      (prevState) => ({ showActualVsPlannedView: !prevState.showActualVsPlannedView }),
      () => {
        closeNavigationView(CONTAINERS.MAP_VIEW);
        this.recenterMap();
        console.log('customFitToBounds toggleActualVsPlanned recenterMap');
      }
    );
  };

  handleActualVsPlanned = (shouldShow) => {
    const { closeNavigationView } = this.props;
    this.setState({ showActualVsPlannedView: shouldShow }, () => {
      closeNavigationView(CONTAINERS.MAP_VIEW);
      this.recenterMap();
      console.log('customFitToBounds handleActualVsPlanned recenterMap');
    });
  };

  hideActualVsPlannedView = () => {
    const { manageMapViewActions } = this.props;
    manageMapViewActions.clearRunOptions();
    this.onRunSelect({ data: null });
    this.handleActualVsPlanned(false);
  };

  determineToRecenterMap = (prevProps, recenter = true) => {
    const { selectedUserIds: prevSelectedUserIds } = prevProps;
    const { selectedUserIds, tasks } = this.props;
    console.log('selectedUserIds,prevSelectedUserIds', selectedUserIds, prevSelectedUserIds);
    let shouldRecenterMap = selectedUserIds !== prevSelectedUserIds;
    const centerAndZoom = getFinalCenterAndZoomInMapView();
    shouldRecenterMap = shouldRecenterMap || tasks.taskSource === TASK_SOURCE.NON_SUBSCRIPTION;
    if (shouldRecenterMap && recenter && (!centerAndZoom || centerAndZoom?.zoom < 2)) {
      this.recenterMap();
      console.log('customFitToBounds determineToRecenterMap recenterMap');
    }
  };

  loadStopsDictAndPaths = () => {
    const { usersLocationInfo } = this.props;
    let userIdLocationMap = {};
    if (!usersLocationInfo.isLoading) {
      userIdLocationMap = _.keyBy(usersLocationInfo.users, 'id');
    }
    const usersWithLocations = this.props.users.map((user) => ({
      id: user.id,
      [USER_LOCATION_TYPE.CURRENT_LOCATION]: {
        id: userIdLocationMap[user.id] || (user.currentLatitude && user.currentLongitude) ? uuid() : null, // generate id only if current location info are available
        lat: userIdLocationMap[user.id] ? userIdLocationMap[user.id].position[1] : user.currentLatitude,
        lng: userIdLocationMap[user.id] ? userIdLocationMap[user.id].position[0] : user.currentLongitude,
      },
      [USER_LOCATION_TYPE.START_LOCATION]: {
        id: user.startStopId,
        lat: null,
        lng: null,
      },
      [USER_LOCATION_TYPE.END_LOCATION]: {
        id: user.endStopId,
        lat: null,
        lng: null,
      },
      name: `${user.firstName} ${user.lastName}`,
    }));
    const stopsDict = this.groupUsersByStopId(usersWithLocations);
    const {
      selectedDateRange: { startDate, endDate },
    } = this.props;
    const tasksToShow = this.props.tasks.items.filter(
      (t) =>
        isDateInBetweenDateRange({ date: t.startTime, startDate, endDate }) &&
        TaskUtilities.isTaskRegular(t) &&
        t.status !== STATUS_LIST.DRAFT.key
    );
    this.addTaskInformationToStopsDict(tasksToShow, stopsDict);
    const pathObjects = this.getPathObjectsForAllUsers(tasksToShow, usersWithLocations, stopsDict);
    this.getAssetInformationForMissingAssetIds(stopsDict, tasksToShow, usersWithLocations);
    const polygons = this.getPloygons(stopsDict);
    this.setState({ paths: pathObjects, polygons });
  };

  getPloygons = (stopsDict = {}) => {
    const polygons = [];
    const stopTypes = [STOP_TYPE.USER_START_END_STOP, STOP_TYPE.TASK_STOP];
    for (const stopId of Object.keys(stopsDict || {})) {
      const { type, geofence = {}, location = {} } = stopsDict[stopId];
      const { lat, lng } = location;
      if (stopTypes.indexOf(type) > -1 && Object.keys(geofence || {}).length) {
        const polygon = getPolygon(geofence, lat, lng);
        if (polygon) {
          polygons.push({ id: stopId, path: polygon });
        }
      }
    }
    return polygons;
  };

  groupUsersByStopId = (users) => {
    const stopsDict = {};
    const locationTypes = [
      USER_LOCATION_TYPE.START_LOCATION,
      USER_LOCATION_TYPE.END_LOCATION,
      USER_LOCATION_TYPE.CURRENT_LOCATION,
    ];
    users.forEach((user) => {
      for (const locationType of locationTypes) {
        const stopId = user[locationType].id;
        const lat = _.get(user, `${locationType}.lat`) || _.get(this.stopInfoDict, `${stopId}.latitude`);
        const lng = _.get(user, `${locationType}.lng`) || _.get(this.stopInfoDict, `${stopId}.longitude`);
        if (stopId) {
          stopsDict[stopId] = stopsDict[stopId] || {
            stopId,
            stopName:
              locationType === USER_LOCATION_TYPE.CURRENT_LOCATION
                ? user.name
                : _.get(this.stopInfoDict, `${stopId}.name`, 'Loading...'),
            id: locationType === USER_LOCATION_TYPE.CURRENT_LOCATION ? user.id : stopId,
            location: {
              lat,
              lng,
            },
            userStops: [],
            taskList: [],
            sequence: [],
            taskUserSequence: {},
            geofence: {},
            type:
              locationType === USER_LOCATION_TYPE.CURRENT_LOCATION
                ? STOP_TYPE.USER_CURRENT_LOCATION_STOP
                : STOP_TYPE.USER_START_END_STOP,
          };
          const stopObject = stopsDict[stopId];
          stopObject.userStops.push({
            id: user.id,
            name: user.name,
            type: locationType,
          });
        }
      }
    });
    return stopsDict;
  };

  addTaskInformationToStopsDict = (tasks, stopsDict) => {
    tasks.forEach((task) => {
      const taskStopId = _.get(task, 'asset.stop.id');
      const geofence = parseJSONStringToObject(_.get(task, 'asset.stop.geofence', '{}'));
      const lat = _.get(this.stopInfoDict, `${taskStopId}.latitude`, _.get(task, 'asset.stop.latitude'));
      const lng = _.get(this.stopInfoDict, `${taskStopId}.longitude`, _.get(task, 'asset.stop.longitude'));
      if (!taskStopId) return;
      if (!stopsDict[taskStopId]) {
        stopsDict[taskStopId] = {
          stopId: taskStopId,
          stopName: _.get(task, 'asset.stop.name'),
          location: {
            lat,
            lng,
          },
          taskList: [],
          sequence: [],
          taskUserSequence: {},
          geofence,
          type: STOP_TYPE.TASK_STOP,
        };
      }
      stopsDict[taskStopId].taskList.push(task);
    });
    return stopsDict;
  };

  filterOutUserStartEndStops = (stopsDict) => {
    for (const [stopId, stopInfo] of Object.entries(stopsDict)) {
      let flag = stopInfo.type === STOP_TYPE.USER_START_END_STOP;
      const {
        location: { lng, lat },
      } = stopInfo;
      if (!lng || !lat) {
        delete stopsDict[stopId];
      }
      if (flag) {
        flag = !stopInfo.taskList || stopInfo.taskList.length === 0;
      }
      if (flag) {
        delete stopsDict[stopId];
      }
    }
  };

  getAssetInformationForMissingAssetIds = async (stopsDict, tasksToShow, usersWithLocations) => {
    try {
      const { manageMapViewActions } = this.props;
      const realStopIds = Object.values(stopsDict)
        .filter((stop) => stop.type !== STOP_TYPE.USER_CURRENT_LOCATION_STOP)
        .map((s) => s.stopId);
      const stopIdsHavingInfo = Object.keys(this.stopInfoDict);
      const stopIdsHavingMissingInfo = _.difference(realStopIds, stopIdsHavingInfo);
      if (stopIdsHavingMissingInfo.length > 0) {
        const assetResponses = await ApiService.getAssetsByIds(stopIdsHavingMissingInfo);
        this.addAssetInformationToStopDict(assetResponses, tasksToShow, usersWithLocations, stopsDict);
      } else {
        manageMapViewActions.setStopsDict(stopsDict);
      }
    } catch (e) {
      logUtils.logError(e);
    }
  };

  addAssetInformationToStopDict = (assetResponses, tasksToShow, usersWithLocations, stopsDict) => {
    const clonedStopsDict = _.cloneDeep(stopsDict);
    const loadingStopInfoFirstTime = Object.keys(this.stopInfoDict).length === 0;
    const polygons = _.clone(this.state.polygons);
    const polygonDict = _.keyBy(polygons, 'id');
    assetResponses.forEach((asset) => {
      if (!asset) {
        return;
      }
      this.stopInfoDict[asset.id] = asset;
      if (clonedStopsDict[asset.id]) {
        clonedStopsDict[asset.id].stopName = asset.name;
        clonedStopsDict[asset.id].location = {
          lat: asset.latitude ? parseFloat(asset.latitude) : asset.latitude,
          lng: asset.longitude ? parseFloat(asset.longitude) : asset.longitude,
        };
        clonedStopsDict[asset.id].geofence = parseJSONStringToObject(asset.geofence || '{}');
      }
      const polygon = getPolygon(asset.geofence || {}, asset.latitude, asset.longitude);
      if (polygon && polygon.length) {
        polygonDict[asset.id] = { id: asset.id, path: polygon };
      }
    });
    const { manageMapViewActions } = this.props;
    manageMapViewActions.setStopsDict(clonedStopsDict);

    const pathObjects = this.getPathObjectsForAllUsers(tasksToShow, usersWithLocations, stopsDict);
    this.setState({ paths: pathObjects, polygons: Object.values(polygonDict) });

    const centerAndZoom = getFinalCenterAndZoomInMapView();
    // eslint-disable-next-line no-unused-expressions
    if (loadingStopInfoFirstTime && !centerAndZoom) {
      this.recenterMap();
      console.log('customFitToBounds addAssetInformationToStopDict recenterMap');
    }
  };

  getPathObjectsForAllUsers = (tasks, usersWithLocations, stopsDict) => {
    const pathObjects = usersWithLocations.flatMap((user) => {
      const pathObjectsOfAUser = this.getPathForUser(user, tasks, stopsDict);
      // eslint-disable-next-line no-plusplus
      const color = randomColor({
        seed: user.id,
        luminosity: 'bright',
      });
      pathObjectsOfAUser.forEach((pathObject) => {
        pathObject.color = color;
        pathObject.userId = user.id;
      });
      return pathObjectsOfAUser;
    });

    return pathObjects;
  };

  getPathForUser = (user, tasks, stopsDict) => {
    const tasksAssignedToUser = tasks.filter((t) => TaskUtilities.doesTaskBelongToAssigneeId(t, user.id));
    const resolvedTasks = tasksAssignedToUser.filter((t) => t.status === STATUS_LIST.RESOLVED.key);
    const plannedTasks = tasksAssignedToUser.filter(
      (t) =>
        _.isNumber(TaskUtilities.getSequenceNumberForTaskAndAssigneeId(t, user.id)) &&
        t.status !== STATUS_LIST.RESOLVED.key
    );
    const pathsForUser = [];
    const actualPathObject = this.getPathForTasks(resolvedTasks, user, PATH_TYPE.RESOLVED, stopsDict);
    const plannedPathObject = this.getPathForTasks(plannedTasks, user, PATH_TYPE.UNRESOLVED, stopsDict);
    if (actualPathObject) pathsForUser.push(actualPathObject);
    if (plannedPathObject) pathsForUser.push(plannedPathObject);
    return pathsForUser;
  };

  getPathForTasks = (tasks, user, pathType, stopsDict) => {
    const startLocationId =
      pathType === PATH_TYPE.RESOLVED || !this.props.useCurrentLocation
        ? _.get(user, `${USER_LOCATION_TYPE.START_LOCATION}.id`)
        : _.get(user, `${USER_LOCATION_TYPE.CURRENT_LOCATION}.id`);
    const endLocationId =
      // eslint-disable-next-line no-nested-ternary
      pathType === PATH_TYPE.RESOLVED
        ? _.get(user, `${USER_LOCATION_TYPE.CURRENT_LOCATION}.id`)
        : this.props.useTechEndLocation
        ? _.get(user, `${USER_LOCATION_TYPE.END_LOCATION}.id`)
        : null;
    const sortFunction =
      pathType === PATH_TYPE.RESOLVED
        ? (t1, t2) => (moment(t1.resolvedOn).isAfter(moment(t2.resolvedOn)) ? 1 : -1)
        : (t1, t2) =>
            TaskUtilities.getSequenceNumberForTaskAndAssigneeId(t1, user.id) -
            TaskUtilities.getSequenceNumberForTaskAndAssigneeId(t2, user.id);

    const sortedTasks = tasks.sort(sortFunction);

    const pathStops = this.getPathStops(sortedTasks, user, pathType, stopsDict);

    if (pathStops.length > 0) {
      if (startLocationId) {
        pathStops.splice(0, 0, startLocationId);
      }
      if (endLocationId) {
        pathStops.push(endLocationId);
      }
    }
    if (pathStops.length === 0) return null;

    const pathObject = {
      path: pathStops,
      pathType,
    };
    return pathObject;
  };

  getPathStops = (sortedTasks, user, pathType, stopsDict) => {
    const pathStops = [];
    let sequence = 1;
    let prevStopId = null;
    for (let i = 0; i < sortedTasks.length; i += 1) {
      const task = sortedTasks[i];
      const currentStopId = _.get(task, 'asset.stop.id');
      if (!currentStopId) continue;
      if (prevStopId === currentStopId) {
        continue;
      }
      const sequenceForThisTask =
        pathType === PATH_TYPE.RESOLVED
          ? // eslint-disable-next-line no-plusplus
            sequence++
          : TaskUtilities.getSequenceNumberForTaskAndAssigneeId(sortedTasks[i], user.id);
      stopsDict[currentStopId].sequence.push(sequenceForThisTask);
      if (!stopsDict[currentStopId].taskUserSequence[task.id]) {
        stopsDict[currentStopId].taskUserSequence[task.id] = {};
      }
      if (!stopsDict[currentStopId].taskUserSequence[task.id][user.id]) {
        stopsDict[currentStopId].taskUserSequence[task.id][user.id] = [];
      }
      stopsDict[currentStopId].taskUserSequence[task.id][user.id].push(sequenceForThisTask);
      pathStops.push(currentStopId);
      prevStopId = currentStopId;
    }
    return pathStops;
  };

  renderIcons = (iconSize = 32) => {
    const userIcon = {
      url: userIco,
      scaledSize: { width: iconSize, height: iconSize },
      anchor: { x: iconSize / 2, y: iconSize / 2 },
      labelOrigin: { x: iconSize / 2, y: iconSize / 2 },
    };
    return {
      userIcon,
    };
  };

  onStopClick = (stopId) => {
    const { selectedUserIds, manageMapViewActions, resetNavigationView } = this.props;

    setTimeout(() => {
      manageMapViewActions.selectStop(stopId, true);
      resetNavigationView(CONTAINERS.MAP_VIEW, {
        component: StopCard,
        props: { stopId, selectedUserIds },
      });
    });
  };

  onReCenterButtonClicked = () => {
    this.recenterMap();
  };

  recenterMap = (locations) => {
    if (this.mapComponent && this.mapComponent.current) {
      this.mapComponent.current.fitBound(locations);
    }
  };

  onActualVsPlannedButtonClicked = () => {
    this.toggleActualVsPlanned();

    if (this.taskListCard.current) this.taskListCard.current.hideComponent();
  };

  onHideActualVsPlannedCard = () => {
    this.handleActualVsPlanned(false);
  };

  onStopInActualVsPlannedCardClicked = (stop) => {
    this.recenterMap(stop && stop.location && [stop.location]);
    this.onStopClick(stop.stopId);
  };

  onRunSelect = ({ data }) => {
    const {
      manageMapViewActions,
      selectedDateRange: { startDate, endDate },
      selectedUserIds,
    } = this.props;
    if (!data || startDate !== endDate) {
      this.setState({ selectedRun: null });
    } else {
      const { value } = data;
      manageMapViewActions.fetchPlannedRouteForParticularRun(selectedUserIds[0], startDate, value);
      this.setState({ selectedRun: value });
    }
  };

  render() {
    const {
      mapTilerUrl,
      routeData,
      selectedUserIds,
      users,
      selectedDateRange: { startDate, endDate },
      runData,
      stopsDict,
      mapsKey,
      selectedStopId,
      handleTaskSelection,
      handleBulkEdit,
      bulkEditSaved,
      manageMapViewActions: { loadUsersLocationsViaSubscription },
    } = this.props;
    const { selectedRun, showActualVsPlannedView, paths, polygons } = this.state;
    const { userIcon } = this.renderIcons();
    const markers = this.getMarkersOnSelectedUsers(stopsDict, selectedUserIds);
    const pathObjects = this.getPathObjectsForSelectedUsers(stopsDict, paths, selectedUserIds);
    // const polygons = this.getPolygons();
    return (
      <div style={{ height: '100%' }}>
        <MapViewPage
          ref={this.mapComponent}
          paths={pathObjects}
          polygons={polygons}
          userIcon={userIcon}
          hideSequence={selectedUserIds.length !== 1}
          onStopClick={this.onStopClick}
          markers={markers}
          selectedStopId={selectedStopId}
          mapTilerUrl={mapTilerUrl}
          routeData={routeData}
          showActualVsPlannedView={showActualVsPlannedView}
          usersSelected={selectedUserIds}
          mapsKey={mapsKey}
          handleTaskSelection={handleTaskSelection}
          handleBulkEdit={handleBulkEdit}
          bulkEditSaved={bulkEditSaved}
          isLocationServiceEnabled={this.props.isLocationServiceEnabled}
          selectedFieldId={this.props.selectedFieldId}
          loadUsersLocationsViaSubscription={loadUsersLocationsViaSubscription}
          isUserSelected={selectedUserIds?.length > 0}
        />
        <ReCenterButton onButtonClicked={this.onReCenterButtonClicked} />
        {selectedUserIds && selectedUserIds.length === 1 && startDate === endDate && (
          <ActualVsPlannedButton
            onButtonClicked={this.onActualVsPlannedButtonClicked}
            active={showActualVsPlannedView}
          />
        )}
        <CardNavigationComponent container={CONTAINERS.MAP_VIEW} />
        {showActualVsPlannedView && (
          <>
            <PortalComponent>
              <CustomDropdown
                placeholder="Select Run"
                selection
                fluid
                search
                options={runData.runOptions}
                onChange={(event, data) => this.onRunSelect({ event, data })}
                loading={runData.isLoading}
                value={selectedRun || runData.defaultValue}
              />
            </PortalComponent>
            <ActualVsPlannedCard
              overlayDisabled
              top="105px"
              routeData={routeData}
              users={users}
              selectedUserIds={selectedUserIds}
              selectedDate={startDate}
              defaultShow
              onHideComponent={this.onHideActualVsPlannedCard}
              onStopClicked={this.onStopInActualVsPlannedCardClicked}
            />
          </>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { settings, selectedField, selectedRunEngineSettings } = state.appReducer;
  const { routeData, runData, stopsDict, selectedStopId, isStopMarkerClicked, usersLocationInfo } =
    state.manageMapViewReducer;
  return {
    useCurrentLocation: _.get(selectedRunEngineSettings, 'useUserLocation', false),
    useTechEndLocation: !_.get(selectedRunEngineSettings, 'disableTechEndLocation', false),
    mapTilerUrl: _.get(settings, `admin.mapTilerSettings.${selectedField.id}`),
    mapsKey: _.get(settings, `common.googleMapsKey`),
    tasks: state.taskViewPageReducer.tasks,
    routeData,
    runData,
    stopsDict,
    selectedStopId,
    isStopMarkerClicked,
    isLocationServiceEnabled: _.get(
      settings,
      `featureFlags.isAWSLocationEnabled`,
      `featureFlags.useHypertrackWithAI`,
      false
    ),
    selectedFieldId: selectedField.id,
    usersLocationInfo,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    manageMapViewActions: bindActionCreators(manageMapViewActionFunction, dispatch),
    resetNavigationView: bindActionCreators(resetView, dispatch),
    closeNavigationView: bindActionCreators(closeView, dispatch),
  };
}

ManageMapView.propTypes = {
  selectedUserIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  tasks: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    taskSource: PropTypes.string.isRequired,
  }).isRequired,
  users: PropTypes.array.isRequired,
  resetNavigationView: PropTypes.func.isRequired,
  closeNavigationView: PropTypes.func.isRequired,
  stopsDict: PropTypes.object.isRequired,
  manageMapViewActions: PropTypes.shape({
    setStopsDict: PropTypes.func.isRequired,
    selectStop: PropTypes.func.isRequired,
    loadRouteData: PropTypes.func.isRequired,
    loadGeofenceEvents: PropTypes.func.isRequired,
    loadUsersLocations: PropTypes.func.isRequired,
    loadUsersLocationsViaSubscription: PropTypes.func.isRequired,
    clearRunOptions: PropTypes.func.isRequired,
    fetchPlannedRouteForParticularRun: PropTypes.func.isRequired,
  }).isRequired,
  selectedStopId: PropTypes.string,
  selectedFieldId: PropTypes.string,
  usersLocationInfo: PropTypes.shape({
    users: PropTypes.array.isRequired,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  selectedDateRange: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }).isRequired,
  useCurrentLocation: PropTypes.bool,
  useTechEndLocation: PropTypes.bool,
  isLocationServiceEnabled: PropTypes.bool,
  mapTilerUrl: PropTypes.string,
  routeData: PropTypes.object,
  runData: PropTypes.object,
  mapsKey: PropTypes.object,
  handleTaskSelection: PropTypes.func.isRequired,
  handleBulkEdit: PropTypes.func.isRequired,
  bulkEditSaved: PropTypes.bool,
  isStopMarkerClicked: PropTypes.bool,
};

ManageMapView.defaultProps = {
  selectedStopId: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageMapView);
